import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import { Carousel } from '@/design-system/atoms/Carousel';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Theme } from '@/design-system/atoms/Theme';
import { Title } from '@/design-system/atoms/Title';
import { arrayNotEmpty } from '@/utils/arrayNotEmpty';
import window from '@/utils/window';

import styles from './FeaturedCardsHero.module.scss';

/**
 * The Featured Cards Hero which will display a set of featured cards. The Featured Cards Hero component
 * supports a carousel when featured cards are hidden.
 */
export const FeaturedCardsHero = (props) => {
    const {
        className,
        title,
        items,
        theme = Theme.NAME.CONDITIONS,
        imageIllustrationTop,
        imageIllustrationBottom,
        ...otherProps
    } = props;

    //These lines should be uncommented out when Featured Cards Hero supports Illustrations
    // const mediaTop = imageIllustrationTop?.props['media'];
    // const mediaBottom = imageIllustrationBottom?.props['media'];
    const mediaTop = 'image';
    const mediaBottom = 'image';

    const illustrationTop = imageIllustrationTop?.props['illustration'].scene;
    const illustrationBottom = imageIllustrationBottom?.props['illustration'].scene;

    const hasMediaTop = mediaTop && imageIllustrationTop;
    const hasMediaBottom = mediaBottom && imageIllustrationBottom;

    const ref = React.useRef();
    const mediaRef = React.useRef();

    const [carouselNavigationRef, setCarouselNavigationRef] = React.useState();

    React.useLayoutEffect(() => {
        if (!hasMediaBottom) {
            return;
        }

        const handleResize = () => {
            if (!mediaRef.current) {
                return;
            }

            const navigationInExists = document.body.contains(carouselNavigationRef);

            const { height } = navigationInExists
                ? carouselNavigationRef.getBoundingClientRect()
                : { height: 0 };

            const { left, width } = mediaRef.current.getBoundingClientRect();

            const mediaLeft = navigationInExists
                ? `${Math.floor(left)}px`
                : `${Math.ceil(left + width)}px`;
            const mediaTransform = navigationInExists ? '0px' : '-100%';
            const carouselNavigationHeight = `${Math.ceil(height)}px`;

            ref.current.style.setProperty('--featured-cards-hero-media-left', mediaLeft);
            ref.current.style.setProperty('--featured-cards-hero-media-transform', mediaTransform);
            ref.current.style.setProperty('--carousel-navigation-height', carouselNavigationHeight);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [hasMediaBottom, carouselNavigationRef]);

    return (
        <div className={styles['featured-cards-hero__wrapper']}>
            <Theme name={theme}>
                <div
                    ref={ref}
                    className={classnames(
                        styles['featured-cards-hero'],
                        hasMediaBottom && styles['featured-cards-hero--media'],
                        className,
                    )}
                    {...otherProps}
                >
                    {hasMediaBottom && (
                        <ContentContainer>
                            <div ref={mediaRef} />
                            <div
                                className={classnames(
                                    styles['featured-cards-hero__media'],
                                    styles['featured-cards-hero__media--bottom-right'],
                                )}
                            >
                                {mediaBottom === 'illustration' && illustrationBottom && (
                                    <>
                                        {/* Illustration FPO */}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 340 190"
                                        >
                                            <rect width="340" height="190" fill="#cccccc"></rect>
                                            <text x="50%" y="50%" textAnchor="middle">
                                                162x162
                                            </text>
                                        </svg>
                                    </>
                                )}

                                {imageIllustrationBottom}
                            </div>
                        </ContentContainer>
                    )}

                    {title && (
                        <div
                            className={classnames(
                                styles['featured-cards-hero__header'],
                                hasMediaTop && styles['featured-cards-hero__header--media'],
                            )}
                        >
                            <ContentContainer>
                                <Title
                                    className={styles['featured-cards-hero__title']}
                                    tag={Title.TAG.H1}
                                    variant={Title.VARIANT.H2}
                                    content={title}
                                    lineClamp={{ sm: 4, lg: 1 }}
                                />
                            </ContentContainer>

                            {mediaTop === 'illustration' && illustrationTop && (
                                <>
                                    {/* Illustration FPO */}
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 340 190">
                                        <rect width="340" height="190" fill="#cccccc"></rect>
                                        <text x="50%" y="50%" textAnchor="middle">
                                            162x162
                                        </text>
                                    </svg>
                                </>
                            )}

                            {mediaTop === 'image' && imageIllustrationTop && (
                                <div className={styles['featured-cards-hero__media--top-right']}>
                                    {imageIllustrationTop}
                                </div>
                            )}
                        </div>
                    )}

                    {arrayNotEmpty(items) && (
                        <Carousel.Root>
                            <Carousel.Content
                                className={styles['featured-cards-hero__carousel']}
                                items={items}
                            />
                            <Carousel.Footer>
                                <Carousel.Spacer />
                                <Carousel.Navigation
                                    ref={setCarouselNavigationRef}
                                    analytics={{
                                        componentName: 'Featured Cards Carousel',
                                        componentTitle: title,
                                    }}
                                />
                            </Carousel.Footer>
                        </Carousel.Root>
                    )}
                </div>
            </Theme>
        </div>
    );
};

FeaturedCardsHero.propTypes = {
    /**
     * Title
     */
    title: PropTypes.node.isRequired,
    /**
     * Carousel items
     */
    items: PropTypes.arrayOf(PropTypes.node),

    /**
     * Image Top
     */
    imageIllustrationTop: PropTypes.node,

    /**
     * Image Bottom
     */
    imageIllustrationBottom: PropTypes.node,
    /**
     * theme
     */
    theme: PropTypes.oneOf(Object.values(Theme.NAME)),
};

FeaturedCardsHero.Card = Carousel.Card;

export default FeaturedCardsHero;
